import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import exampleImage from '../assets/PersonalTeste.jpg';
import { FaDumbbell, FaHeartbeat, FaUsers, FaTrophy } from 'react-icons/fa';

const OurServices = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const features = [
    { icon: FaDumbbell, title: 'Equipamentos Premium', desc: 'Tecnologia de ponta' },
    { icon: FaHeartbeat, title: 'Treino Personalizado', desc: 'Para seus objetivos' },
    { icon: FaUsers, title: 'Aulas em Grupo', desc: 'Motivação coletiva' },
    { icon: FaTrophy, title: 'Resultados Reais', desc: 'Transformação garantida' }
  ];

  return (
    <Container id="servicos" ref={sectionRef}>
      <BackgroundGlow />
      <Content>
        <LeftSection $isVisible={isVisible}>
          <SectionBadge>
            <BadgeIcon>✨</BadgeIcon>
            <span>SOBRE NÓS</span>
          </SectionBadge>
          
          <Title>
            ALCANCE O <HighlightText>CORPO FÍSICO</HighlightText> QUE VOCÊ MERECE
          </Title>
          
          <Description>
            Nossa academia oferece uma ampla gama de serviços para atender a todas 
            as suas necessidades de fitness. Desde programas de cardio até personal 
            trainers especializados, estamos aqui para ajudá-lo a atingir seus objetivos. 
            Contamos com equipamentos de ponta e um ambiente amigável e acolhedor.
          </Description>

          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard 
                key={index} 
                $delay={`${0.2 + index * 0.1}s`}
                $isVisible={isVisible}
              >
                <FeatureIconWrapper>
                  <feature.icon />
                  <FeatureGlow />
                </FeatureIconWrapper>
                <FeatureContent>
                  <FeatureTitle>{feature.title}</FeatureTitle>
                  <FeatureDesc>{feature.desc}</FeatureDesc>
                </FeatureContent>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </LeftSection>

        <RightSection $isVisible={isVisible}>
          <ImageWrapper>
            <ImageBorder />
            <ImageGlow />
            <Image src={exampleImage} alt="Academia M30 Fitness" />
            <ImageOverlay />
            
            <ExperienceBadge>
              <ExperienceNumber>4 anos+</ExperienceNumber>
              <ExperienceText>Transformando<br /> Vidas</ExperienceText>
            </ExperienceBadge>
          </ImageWrapper>
        </RightSection>
      </Content>
    </Container>
  );
};

export default OurServices;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
`;

const borderRotate = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const Container = styled.section`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: var(--bg-dark);
  position: relative;
  padding: 60px 0;
  overflow: hidden;
`;

const BackgroundGlow = styled.div`
  position: absolute;
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, var(--primary-glow) 0%, transparent 70%);
  top: -200px;
  right: -200px;
  opacity: 0.3;
  pointer-events: none;
`;

const Content = styled.div`
  width: 90%;
  max-width: 1200px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 60px;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 50px;
  }
`;

const LeftSection = styled.div`
  flex: 1;
  opacity: 0;
  
  ${({ $isVisible }) => $isVisible && css`
    animation: ${fadeInUp} 0.8s ease forwards;
  `}

  @media (max-width: 900px) {
    text-align: center;
  }
`;

const SectionBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: rgba(229, 168, 0, 0.1);
  border: 1px solid rgba(229, 168, 0, 0.2);
  border-radius: 50px;
  padding: 10px 20px;
  margin-bottom: 25px;

  span {
    font-family: 'Rajdhani', sans-serif;
    font-weight: 600;
    font-size: 0.85rem;
    color: var(--primary-color);
    letter-spacing: 3px;
  }
`;

const BadgeIcon = styled.span`
  font-size: 1rem;
`;

const Title = styled.h2`
  font-family: 'Orbitron', sans-serif;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  color: #fff;
  line-height: 1.2;
  margin-bottom: 25px;
  letter-spacing: 1px;
`;

const HighlightText = styled.span`
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Description = styled.p`
  font-family: 'Rajdhani', sans-serif;
  font-size: 1.1rem;
  color: #888;
  line-height: 1.8;
  margin-bottom: 40px;

  @media (max-width: 900px) {
    text-align: left;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  border-radius: 16px;
  padding: 20px;
  transition: all 0.3s ease;
  opacity: 0;
  
  ${({ $isVisible, $delay }) => $isVisible && css`
    animation: ${fadeInUp} 0.6s ease forwards;
    animation-delay: ${$delay};
  `}

  &:hover {
    background: rgba(229, 168, 0, 0.05);
    border-color: rgba(229, 168, 0, 0.3);
    transform: translateY(-5px);
  }
`;

const FeatureIconWrapper = styled.div`
  position: relative;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(229, 168, 0, 0.1);
  border-radius: 12px;
  color: var(--primary-color);
  font-size: 1.3rem;
  flex-shrink: 0;
`;

const FeatureGlow = styled.div`
  position: absolute;
  inset: 0;
  background: var(--primary-color);
  border-radius: 12px;
  filter: blur(15px);
  opacity: 0;
  transition: opacity 0.3s ease;

  ${FeatureCard}:hover & {
    opacity: 0.3;
  }
`;

const FeatureContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const FeatureTitle = styled.h4`
  font-family: 'Rajdhani', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 3px;
`;

const FeatureDesc = styled.span`
  font-family: 'Rajdhani', sans-serif;
  font-size: 0.85rem;
  color: #666;
`;

const RightSection = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  opacity: 0;
  
  ${({ $isVisible }) => $isVisible && css`
    animation: ${fadeInRight} 0.8s ease 0.3s forwards;
  `}
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 500px;
`;

const ImageBorder = styled.div`
  position: absolute;
  inset: -5px;
  border-radius: 20px;
  padding: 3px;
  background: linear-gradient(135deg, #b8860b, var(--primary-color), #ffd700, #ffe066, var(--primary-color));
  background-size: 200% 200%;
  animation: ${borderRotate} 4s linear infinite;
  z-index: 0;
  
  &::before {
    content: '';
    position: absolute;
    inset: 3px;
    background: var(--bg-dark);
    border-radius: 18px;
  }
`;

const ImageGlow = styled.div`
  position: absolute;
  inset: 0;
  background: var(--primary-color);
  border-radius: 20px;
  filter: blur(40px);
  opacity: 0.3;
  z-index: 0;
  animation: ${pulse} 4s ease-in-out infinite;
`;

const Image = styled.img`
  position: relative;
  width: 100%;
  height: 600px;
  object-fit: cover;
  border-radius: 18px;
  z-index: 1;
`;

const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    transparent 0%,
    transparent 60%,
    rgba(10, 10, 10, 0.8) 100%
  );
  border-radius: 18px;
  z-index: 2;
`;

const ExperienceBadge = styled.div`
  position: absolute;
  bottom: 30px;
  left: -30px;
  background: rgba(20, 20, 20, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(229, 168, 0, 0.3);
  border-radius: 16px;
  padding: 20px 25px;
  display: flex;
  align-items: center;
  gap: 15px;
  z-index: 3;
  animation: ${float} 3s ease-in-out infinite;

  @media (max-width: 600px) {
    left: 10px;
  }
`;

const ExperienceNumber = styled.span`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.5rem;
  font-weight: 900;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const ExperienceText = styled.span`
  font-family: 'Rajdhani', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  color: #888;
  line-height: 1.3;
`;
